import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Package,
  TrendingDown,
} from 'lucide-react';
import { inventoryAPI } from '../../services/inventoryAPI';
import { InventoryStockAlert } from '../../types/inventory';

interface LowStockAlertsProps {
  onRefresh: () => void;
}

const LowStockAlerts: React.FC<LowStockAlertsProps> = ({ onRefresh }) => {
  const { t } = useLanguage();
  const [alerts, setAlerts] = useState<InventoryStockAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unacknowledged'>('unacknowledged');

  useEffect(() => {
    fetchAlerts();
  }, [filter]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getStockAlerts(
        filter === 'unacknowledged' ? false : undefined
      );
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    try {
      await inventoryAPI.acknowledgeAlert(alertId);
      await fetchAlerts();
      onRefresh();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const getAlertIcon = (alertType: string) => {
    switch (alertType) {
      case 'out_of_stock':
        return { icon: AlertTriangle, color: 'text-red-600' };
      case 'low_stock':
        return { icon: TrendingDown, color: 'text-orange-600' };
      case 'reorder_point':
        return { icon: Package, color: 'text-yellow-600' };
      default:
        return { icon: AlertTriangle, color: 'text-gray-600' };
    }
  };

  const getAlertTypeLabel = (alertType: string) => {
    switch (alertType) {
      case 'out_of_stock':
        return t('inventory.alerts.outOfStock');
      case 'low_stock':
        return t('inventory.alerts.lowStock');
      case 'reorder_point':
        return t('inventory.alerts.reorderPoint');
      default:
        return alertType;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter and Refresh */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => setFilter('unacknowledged')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'unacknowledged'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('inventory.alerts.unacknowledged')}
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('inventory.alerts.all')}
          </button>
        </div>
        <button
          onClick={fetchAlerts}
          className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.refresh')}
        </button>
      </div>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('inventory.alerts.noAlerts')}
          </h3>
          <p className="text-gray-500">{t('inventory.alerts.noAlertsDescription')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {alerts.map((alert) => {
              const alertIcon = getAlertIcon(alert.alert_type);
              return (
                <div
                  key={alert.id}
                  className={`p-6 ${
                    alert.is_acknowledged ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
                  } transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-2 rounded-lg ${
                        alert.is_acknowledged ? 'bg-gray-200' : 'bg-red-50'
                      }`}>
                        <alertIcon.icon
                          className={`w-6 h-6 ${
                            alert.is_acknowledged ? 'text-gray-500' : alertIcon.color
                          }`}
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {alert.item?.name || 'Unknown Item'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          SKU: {alert.item?.sku || 'N/A'}
                        </p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t('inventory.alerts.type')}:
                            </span>{' '}
                            <span className={alertIcon.color}>
                              {getAlertTypeLabel(alert.alert_type)}
                            </span>
                          </p>
                          <p className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t('inventory.alerts.currentQuantity')}:
                            </span>{' '}
                            {alert.current_quantity} {alert.item?.unit_of_measure || 'units'}
                          </p>
                          <p className="text-sm">
                            <span className="font-medium text-gray-700">
                              {t('inventory.alerts.threshold')}:
                            </span>{' '}
                            {alert.threshold_quantity} {alert.item?.unit_of_measure || 'units'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {t('inventory.alerts.created')}:{' '}
                            {new Date(alert.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {alert.is_acknowledged && alert.acknowledged_at && (
                          <div className="mt-2 text-sm text-gray-500">
                            {t('inventory.alerts.acknowledgedAt')}:{' '}
                            {new Date(alert.acknowledged_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    {!alert.is_acknowledged && (
                      <button
                        onClick={() => handleAcknowledge(alert.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {t('inventory.alerts.acknowledge')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LowStockAlerts;